/**
 * @file     FlowChip.tsx
 * @readme   ../README.md
 * @purpose  The step-flow answer chip: a native toggle button at least 44px tall that announces its state through aria-pressed and shows it with a filled brand surface, never colour alone (weight changes too).
 */
'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { flowColors, flowFocusRing, flowRadius, flowTransition, reducedMotion } from './flowKitTokens';

type FlowChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
  selected: boolean;
  icon?: ReactNode;
  /** Stretch to fill a grid cell instead of sizing to the label. */
  block?: boolean;
  sx?: SxProps<Theme>;
};

export const FlowChip = ({ selected, icon, block = false, sx, children, type = 'button', ...rest }: FlowChipProps) => (
  <Box
    component="button"
    type={type}
    aria-pressed={selected}
    {...rest}
    sx={[
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: icon ? 'flex-start' : 'center',
        gap: 1,
        minHeight: 48,
        minWidth: 48,
        width: block ? '100%' : 'auto',
        px: 4,
        py: 1,
        m: 0,
        border: `1px solid ${selected ? flowColors.interactivePrimary : flowColors.borderStrong}`,
        borderRadius: `${flowRadius.md}px`,
        backgroundColor: selected ? flowColors.interactivePrimary : flowColors.surfaceCard,
        color: selected ? flowColors.textOnPrimary : flowColors.textPrimary,
        font: 'inherit',
        fontSize: 15,
        fontWeight: selected ? 600 : 500,
        lineHeight: 1.25,
        textAlign: icon ? 'start' : 'center',
        cursor: 'pointer',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transition: flowTransition('background-color', 'border-color', 'color'),
        '&:hover': { borderColor: flowColors.interactivePrimary },
        '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
        ...flowFocusRing,
        [reducedMotion]: { transition: 'none' },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {icon ? (
      <Box
        component="span"
        aria-hidden="true"
        sx={{ display: 'inline-flex', flexShrink: 0, color: selected ? flowColors.textOnPrimary : flowColors.textSecondary }}
      >
        {icon}
      </Box>
    ) : null}
    {children}
  </Box>
);
